/**
 * KI-Nutzung — Verbrauch und Kosten der Modellaufrufe.
 *
 * Datenquelle ist `GET /api/ai-usage`, gespeist aus der Tabelle `ai_usage`,
 * die je Aufruf eine Zeile hält.
 *
 * Zur Einordnung der Zahlen: Anthropic liefert über die API keine Kosten,
 * sondern nur Token-Zahlen. Gespeichert werden deshalb ausschließlich Tokens;
 * der Preis wird im Backend aus einer Preistabelle gerechnet. Abgerechnet wird
 * in US-Dollar — die Euro-Beträge sind eine Umrechnung und als solche
 * gekennzeichnet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  BanknotesIcon,
  ChartBarIcon,
  ClockIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

interface Zeitreihe {
  tag: string;
  aufrufe: number;
  tokens: number;
  kosten_usd: number;
  kosten_eur: number;
}

interface Auswertung {
  zeitraum_tage: number;
  usd_zu_eur: number;
  gesamt: {
    aufrufe: number;
    fehlversuche: number;
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    cache_write_tokens: number;
    kosten_usd: number;
    kosten_eur: number;
    dauer_schnitt_ms: number;
    kosten_je_aufruf_usd: number;
    erster: string | null;
    letzter: string | null;
  };
  verlauf: Zeitreihe[];
  nach_modell: any[];
  nach_zweck: any[];
  teuerste: any[];
  preise: Record<string, { anzeige: string; input: number; output: number; hinweis?: string }>;
}

const ZEITRAEUME = [
  { tage: 7, label: '7 Tage' },
  { tage: 30, label: '30 Tage' },
  { tage: 90, label: '90 Tage' },
  { tage: 365, label: '1 Jahr' },
];

const ZWECK_LABELS: Record<string, string> = {
  beleganalyse: 'Belege analysieren',
  buchungskategorie: 'Buchung kategorisieren',
  buchungskategorien_batch: 'Buchungen gesammelt kategorisieren',
  anlagen_assistent: 'Anlagen-Assistent',
  anlagen_assistent_frage: 'Rückfrage im Anlagen-Assistenten',
};

/** Tokenzahlen kompakt: 1.247.000 -> "1,25 Mio." */
const tokenKurz = (n: number): string => {
  const z = Number(n) || 0;
  if (z >= 1_000_000) return `${(z / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} Mio.`;
  if (z >= 1_000) return `${(z / 1_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Tsd.`;
  return z.toLocaleString('de-DE');
};

/**
 * Kosten anzeigen. Beträge unter einem Cent würden auf "0,00 €" gerundet und
 * sähen aus wie "kostenlos" — deshalb bekommen sie mehr Nachkommastellen.
 */
const euro = (n: number): string => {
  const z = Number(n) || 0;
  if (z > 0 && z < 0.01) return `${z.toLocaleString('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} €`;
  return z.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const dollar = (n: number): string =>
  '$' + (Number(n) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Lokales Kalenderdatum als stabiler Map-Schluessel. */
const tagSchluessel = (wert: string | Date): string => {
  const datum = wert instanceof Date ? wert : new Date(wert);
  return [
    datum.getFullYear(),
    String(datum.getMonth() + 1).padStart(2, '0'),
    String(datum.getDate()).padStart(2, '0'),
  ].join('-');
};

const Kennzahl: React.FC<{
  icon: React.ElementType;
  farbe: string;
  titel: string;
  wert: string;
  zusatz?: string;
}> = ({ icon: Icon, farbe, titel, wert, zusatz }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432]">
    <div className="flex items-start gap-3">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${farbe}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {titel}
        </p>
        <p className="mt-0.5 truncate text-[22px] font-bold leading-tight text-gray-900 dark:text-white">
          {wert}
        </p>
        {zusatz && <p className="text-[11.5px] text-gray-400 dark:text-gray-500">{zusatz}</p>}
      </div>
    </div>
  </div>
);

const AiUsage: React.FC = () => {
  const [daten, setDaten] = useState<Auswertung | null>(null);
  const [tage, setTage] = useState(30);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const holen = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    try {
      const res = await apiClient.get(`/api/ai-usage?tage=${tage}`);
      if (!res.data?.success) {
        setFehler(res.data?.error || 'Die Auswertung konnte nicht geladen werden.');
        return;
      }
      setDaten(res.data);
    } catch (e) {
      console.error('AiUsage: Laden fehlgeschlagen', e);
      setFehler('Die Auswertung konnte nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }, [tage]);

  useEffect(() => {
    holen();
  }, [holen]);

  const g = daten?.gesamt;
  // Die API liefert nur Tage mit Aufrufen. Fuer einen echten Zeitverlauf wird
  // der komplette gewaehlte Zeitraum aufgefuellt; fehlende Tage sind 0.
  const verlaufNachTag = new Map(
    (daten?.verlauf || []).map((eintrag) => [tagSchluessel(eintrag.tag), eintrag])
  );
  const heute = new Date();
  heute.setHours(12, 0, 0, 0);
  const verlaufVoll: Zeitreihe[] = Array.from({ length: tage }, (_, index) => {
    const datum = new Date(heute);
    datum.setDate(heute.getDate() - (tage - 1 - index));
    const schluessel = tagSchluessel(datum);
    return verlaufNachTag.get(schluessel) || {
      tag: schluessel,
      aufrufe: 0,
      tokens: 0,
      kosten_usd: 0,
      kosten_eur: 0,
    };
  });
  const maxKosten = Math.max(...verlaufVoll.map(v => Number(v.kosten_usd) || 0), 0.000001);
  const diagramm = { breite: 1000, hoehe: 180, links: 20, rechts: 20, oben: 12, unten: 18 };
  const nutzBreite = diagramm.breite - diagramm.links - diagramm.rechts;
  const nutzHoehe = diagramm.hoehe - diagramm.oben - diagramm.unten;
  const graphPunkte = verlaufVoll.map((eintrag, index) => ({
    ...eintrag,
    x: diagramm.links + (index / Math.max(verlaufVoll.length - 1, 1)) * nutzBreite,
    y: diagramm.oben + (1 - (Number(eintrag.kosten_usd) || 0) / maxKosten) * nutzHoehe,
  }));
  const linienPunkte = graphPunkte.map(punkt => `${punkt.x},${punkt.y}`).join(' ');
  const flaechenPunkte = `${diagramm.links},${diagramm.oben + nutzHoehe} ${linienPunkte} ${diagramm.links + nutzBreite},${diagramm.oben + nutzHoehe}`;

  /**
   * Number() vor jeder Addition — MySQL liefert SUM() als String, und
   * "3018" + "917" ergäbe die Verkettung "3018917" statt 3935. Das Backend
   * wandelt bereits um; diese Absicherung kostet nichts und verhindert, dass
   * ein einzelnes vergessenes Feld die Zahlen unbemerkt verfälscht.
   */
  const n = (v: unknown): number => Number(v) || 0;

  const tokenGesamt = g ? n(g.input_tokens) + n(g.output_tokens) + n(g.cache_read_tokens) : 0;
  const eingabeGesamt = g ? n(g.input_tokens) + n(g.cache_read_tokens) : 0;

  /** Anteil der Token, die aus dem Cache kamen — das ist der Hebel bei den Kosten. */
  const cacheAnteil =
    eingabeGesamt > 0 ? Math.round((n(g!.cache_read_tokens) / eingabeGesamt) * 100) : 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-[22px] font-bold text-gray-900 dark:text-white">
            <SparklesIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
            KI-Nutzung
          </h1>
          <p className="mt-0.5 text-[13px] text-gray-500 dark:text-gray-400">
            Verbrauch und Kosten der Modellaufrufe
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
            {ZEITRAEUME.map(z => (
              <button
                key={z.tage}
                type="button"
                onClick={() => setTage(z.tage)}
                className={`rounded-md px-2.5 py-1 text-[12px] font-bold transition-colors ${
                  tage === z.tage
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {z.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={holen}
            disabled={laden}
            aria-label="Neu laden"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className={`h-4 w-4 ${laden ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
        </div>
      </div>

      {fehler && (
        <p className="mb-4 rounded-lg bg-error-50 px-3 py-2 text-sm font-semibold text-error-700 dark:bg-error-900/20 dark:text-error-300">
          {fehler}
        </p>
      )}

      {laden && !daten ? (
        <p className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">Auswertung wird geladen …</p>
      ) : !g || g.aufrufe === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 py-16 text-center dark:border-gray-600">
          <SparklesIcon className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            In diesem Zeitraum wurde die KI nicht genutzt.
          </p>
          <p className="mt-1 text-[12px] text-gray-400 dark:text-gray-500">
            Aufrufe werden ab sofort erfasst — bereits vorher erfolgte Analysen fehlen hier.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Kennzahlen */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kennzahl
              icon={BanknotesIcon}
              farbe="bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
              titel="Kosten"
              wert={euro(g.kosten_eur)}
              zusatz={`${dollar(g.kosten_usd)} · Kurs ${daten!.usd_zu_eur}`}
            />
            <Kennzahl
              icon={SparklesIcon}
              farbe="bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300"
              titel="Aufrufe"
              wert={n(g.aufrufe).toLocaleString('de-DE')}
              zusatz={
                n(g.fehlversuche) > 0
                  ? `${n(g.fehlversuche)} fehlgeschlagen`
                  : `im Schnitt ${euro(n(g.kosten_eur) / Math.max(n(g.aufrufe), 1))} je Aufruf`
              }
            />
            <Kennzahl
              icon={CpuChipIcon}
              farbe="bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300"
              titel="Token"
              wert={tokenKurz(tokenGesamt)}
              zusatz={`${cacheAnteil} % aus dem Cache`}
            />
            <Kennzahl
              icon={ClockIcon}
              farbe="bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              titel="Dauer"
              wert={`${(n(g.dauer_schnitt_ms) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s`}
              zusatz="Durchschnitt je Aufruf"
            />
          </div>

          {/* Hinweis zum Cache: das ist der eigentliche Kostenhebel */}
          {cacheAnteil > 0 && (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[12.5px] text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
              <strong className="font-bold">{cacheAnteil} %</strong> der Eingabe-Token kamen aus dem
              Prompt-Cache und kosten dort nur etwa ein Zehntel. Möglich ist das, weil die
              Analyse-Anweisung bei jedem Beleg identisch ist.
            </p>
          )}

          {/* Verlauf */}
          {daten!.verlauf.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432]">
              <div className="mb-3 flex items-center gap-2">
                <ChartBarIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                <h2 className="text-[13px] font-bold text-gray-900 dark:text-white">Verlauf</h2>
              </div>
              <div className="relative">
                <div className="mb-1 flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500">
                  <span>{euro(maxKosten)} / Tag</span>
                  <span>{tage} Tage</span>
                </div>
                <svg
                  viewBox={`0 0 ${diagramm.breite} ${diagramm.hoehe}`}
                  preserveAspectRatio="none"
                  className="h-40 w-full overflow-visible text-primary-600 dark:text-primary-400"
                  role="img"
                  aria-label={`Linienverlauf der KI-Kosten über ${tage} Tage`}
                >
                  {[diagramm.oben, diagramm.oben + nutzHoehe / 2, diagramm.oben + nutzHoehe].map((y, index) => (
                    <line
                      key={y}
                      x1={diagramm.links}
                      x2={diagramm.links + nutzBreite}
                      y1={y}
                      y2={y}
                      stroke="currentColor"
                      strokeDasharray={index === 1 ? '5 5' : undefined}
                      vectorEffect="non-scaling-stroke"
                      className="text-gray-200 dark:text-gray-700"
                    />
                  ))}
                  <polygon points={flaechenPunkte} fill="currentColor" opacity="0.08" />
                  <polyline
                    points={linienPunkte}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  {graphPunkte.filter(punkt => punkt.aufrufe > 0).map(punkt => (
                    <circle
                      key={punkt.tag}
                      cx={punkt.x}
                      cy={punkt.y}
                      r={tage <= 30 ? 5 : 3.5}
                      fill="currentColor"
                      stroke="white"
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    >
                      <title>
                        {new Date(punkt.tag).toLocaleDateString('de-DE')} · {punkt.aufrufe}× · {euro(punkt.kosten_eur)}
                      </title>
                    </circle>
                  ))}
                </svg>
                <div className="mt-1 grid grid-cols-3 text-[10px] text-gray-400 dark:text-gray-500">
                  {[verlaufVoll[0], verlaufVoll[Math.floor((verlaufVoll.length - 1) / 2)], verlaufVoll[verlaufVoll.length - 1]].map((punkt, index) => (
                    <span key={`${punkt.tag}-${index}`} className={index === 1 ? 'text-center' : index === 2 ? 'text-right' : ''}>
                      {new Date(punkt.tag).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        ...(tage > 90 ? { year: '2-digit' } : {}),
                      })}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Nach Zweck und Modell */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
              <h2 className="border-b border-gray-200 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                Nach Verwendung
              </h2>
              {daten!.nach_zweck.map(z => (
                <div
                  key={z.zweck}
                  className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 last:border-b-0 dark:border-gray-700/60"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                      {ZWECK_LABELS[z.zweck] || z.zweck}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {z.aufrufe}× · ⌀ {(Number(z.dauer_schnitt_ms) / 1000).toFixed(1)} s
                      {Number(z.fehlversuche) > 0 && (
                        <span className="text-warning-600 dark:text-warning-400">
                          {' '}· {z.fehlversuche} fehlgeschlagen
                        </span>
                      )}
                    </p>
                  </div>
                  <span className="shrink-0 text-[13px] font-bold text-gray-900 dark:text-white">
                    {euro(z.kosten_eur)}
                  </span>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
              <h2 className="border-b border-gray-200 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                Nach Modell
              </h2>
              {daten!.nach_modell.map(m => {
                const preis = m.preis || daten!.preise[m.model];
                return (
                  <div
                    key={m.model}
                    className="border-b border-gray-100 px-4 py-2.5 last:border-b-0 dark:border-gray-700/60"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                        {preis?.anzeige || m.model}
                      </p>
                      <span className="shrink-0 text-[13px] font-bold text-gray-900 dark:text-white">
                        {euro(m.kosten_eur)}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-400">
                      {m.aufrufe}× · {tokenKurz(Number(m.input_tokens))} ein /{' '}
                      {tokenKurz(Number(m.output_tokens))} aus
                      {preis && ` · $${preis.input} / $${preis.output} je Mio.`}
                    </p>
                    {preis?.hinweis && (
                      <p className="mt-0.5 text-[10.5px] text-gray-400 dark:text-gray-500">{preis.hinweis}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Teuerste Aufrufe */}
          {daten!.teuerste.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
              <h2 className="border-b border-gray-200 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                Teuerste Aufrufe
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <tbody>
                    {daten!.teuerste.map(t => (
                      <tr key={t.id} className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/60">
                        <td className="px-4 py-2 text-gray-400 dark:text-gray-500">
                          {new Date(t.created_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="max-w-[280px] truncate px-2 py-2 text-gray-900 dark:text-white">
                          {t.dokument_name || <span className="text-gray-400">ohne Beleg</span>}
                          {!t.erfolg && (
                            <ExclamationTriangleIcon
                              className="ml-1 inline h-3.5 w-3.5 text-warning-500"
                              aria-label="fehlgeschlagen"
                            />
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right text-gray-400">
                          {tokenKurz(Number(t.input_tokens) + Number(t.output_tokens))} Token
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-right font-bold text-gray-900 dark:text-white">
                          {euro(t.kosten_eur)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">
            Abgerechnet wird in US-Dollar; die Euro-Beträge sind mit dem Kurs {daten!.usd_zu_eur}{' '}
            umgerechnet und daher Näherungswerte. Die API liefert keine Kosten, sondern nur
            Token-Zahlen — gespeichert werden ausschließlich diese, der Preis wird beim Auswerten
            gerechnet. Ältere Aufrufe erscheinen damit stets zu heutigen Preisen.
          </p>
        </div>
      )}
    </div>
  );
};

export default AiUsage;
